import React, { useState, useEffect } from "react";
import { Table, Card, Tabs, Button, InputNumber, message, Space, Alert, Image, Typography, Row, Col, Divider, Switch, Statistic } from "antd";
import { CalculatorOutlined, TableOutlined, LineChartOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;

const ErlangKServiceTimes = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("input");
  const [inputData, setInputData] = useState({
    arrival_rate_lambda: 4,
    service_rate_mu: 5,
    number_of_servers: 1,
    k_erlang: 2,
    use_cost: false,
    server_cost_per_time: 0,
    waiting_cost_per_time: 0
  });
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const getAuthToken = () => localStorage.getItem("access_token");

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    window.location.reload();
  };

  useEffect(() => {
    if (fileId) {
      loadData(fileId);
    } else if (initialConfig) {
      setInputData({
        ...inputData,
        ...initialConfig
      });
      setResults(null);
      setIsSaved(false);
      setActiveTab("input");
    }
  }, [fileId, initialConfig]);

  const loadData = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");
      setIsLoading(true);

      const res = await axios.get(
        `/api/retrieve_Erlang_k/${id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setInputData(res.data.input_data);
      setResults(res.data.output_data);
      setIsSaved(true);
      setActiveTab("output");
    } catch (err) {
      console.error("Load error:", err);
      if (err.response?.status === 401) handleLogout();
      message.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const saveData = async () => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");

      setIsLoading(true);

      const payload = {
        name: fileName || "Erlang-k Service Times Analysis",
        input_data: inputData,
      };

      let res;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (fileId) {
        res = await axios.put(
          `/api/update_Erlang_k/${fileId}/`,
          payload,
          config
        );
      } else {
        res = await axios.post(
          `/api/save_Erlang_k/`,
          payload,
          config
        );
      }

      setResults(res.data.output_data);

      // Update selectedFile.id immediately after saving to prevent duplicate entries
      if (!fileId && setSelectedFile) {
        setSelectedFile({
          id: res.data.id,
          name: res.data.name,
          type: "erlang_k",
          inputData: res.data.input_data
        });
      }

      setIsSaved(true);
      setActiveTab("output");
      message.success(fileId ? "Updated successfully" : "Saved successfully");
    } catch (err) {
      console.error("Save error:", err);
      if (err.response?.status === 401) handleLogout();
      message.error(`Failed to save: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, val) => {
    setInputData({ ...inputData, [field]: val });
  };

  const renderInputForm = () => (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Divider orientation="left">Operational Parameters</Divider>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Arrival Rate (λ):</Text><br />
            <InputNumber
              style={{ width: '100%' }}
              min={0.0001}
              value={inputData.arrival_rate_lambda}
              onChange={(v) => handleInputChange("arrival_rate_lambda", v)}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Service Rate (μ):</Text><br />
            <InputNumber
              style={{ width: '100%' }}
              min={0.0001}
              value={inputData.service_rate_mu}
              onChange={(v) => handleInputChange("service_rate_mu", v)}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Number of Servers (s):</Text><br />
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={0}
              value={inputData.number_of_servers}
              onChange={(v) => handleInputChange("number_of_servers", v)}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Erlang Parameter (k):</Text><br />
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={0}
              value={inputData.k_erlang}
              onChange={(v) => handleInputChange("k_erlang", v)}
            />
          </Col>
        </Row>

        <Divider orientation="left">Cost Analysis</Divider>
        <Row gutter={[24, 24]} align="middle">
          <Col span={24}>
            <Space>
              <Switch
                checked={inputData.use_cost}
                onChange={(v) => handleInputChange("use_cost", v)}
              />
              <Text>Enable Cost Analysis</Text>
            </Space>
          </Col>
          {inputData.use_cost && (
            <>
              <Col xs={24} sm={12}>
                <Text strong>Server Cost per Time Unit ($/hr):</Text><br />
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  value={inputData.server_cost_per_time}
                  onChange={(v) => handleInputChange("server_cost_per_time", v)}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Waiting Cost per Time Unit ($/hr):</Text><br />
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  value={inputData.waiting_cost_per_time}
                  onChange={(v) => handleInputChange("waiting_cost_per_time", v)}
                />
              </Col>
            </>
          )}
        </Row>

        <Button
          type="primary"
          icon={<CalculatorOutlined />}
          onClick={saveData}
          loading={isLoading}
          size="large"
          block
          style={{ height: '50px', marginTop: '20px' }}
        >
          {isSaved ? "Update Analysis" : "Calculate & Save"}
        </Button>
      </Space>
    </Card>
  );

  const renderOutputTable = () => {
    if (!results || !results.results) {
      return (
        <Alert
          message="No Results Available"
          description="Please complete the inputs and click calculate to see the analysis."
          type="info"
          showIcon
        />
      );
    }

    const {
      average_server_utilization,
      average_number_in_line_Lq,
      average_number_in_system_L,
      average_time_in_line_Wq,
      average_time_in_system_W,
      probability_system_empty_P0,
      cost_analysis
    } = results.results;

    const mainMetrics = [
      { key: '1', parameter: 'Average Server Utilization (ρ)', value: average_server_utilization?.toFixed(4) },
      { key: '2', parameter: 'Average Number in Queue (Lq)', value: average_number_in_line_Lq?.toFixed(4) },
      { key: '3', parameter: 'Average Number in System (L)', value: average_number_in_system_L?.toFixed(4) },
      { key: '4', parameter: 'Average Time in Queue (Wq)', value: average_time_in_line_Wq?.value?.toFixed(4) },
      { key: '4-min', parameter: 'Average Time in Queue (Wq) - Minutes', value: `${average_time_in_line_Wq?.minutes?.toFixed(2)} min` },
      { key: '4-sec', parameter: 'Average Time in Queue (Wq) - Seconds', value: `${average_time_in_line_Wq?.seconds?.toFixed(2)} sec` },
      { key: '5', parameter: 'Average Time in System (W)', value: average_time_in_system_W?.value?.toFixed(4) },
      { key: '5-min', parameter: 'Average Time in System (W) - Minutes', value: `${average_time_in_system_W?.minutes?.toFixed(2)} min` },
      { key: '5-sec', parameter: 'Average Time in System (W) - Seconds', value: `${average_time_in_system_W?.seconds?.toFixed(2)} sec` },
      { key: '6', parameter: 'Probability System is Empty (P0)', value: probability_system_empty_P0?.toFixed(4) },
    ];

    const columns = [
      { title: 'Parameter', dataIndex: 'parameter', key: 'parameter', render: (text) => <Text strong>{text}</Text> },
      { title: 'Value', dataIndex: 'value', key: 'value' },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Operational Metrics" size="small">
              <Table
                dataSource={mainMetrics}
                columns={columns}
                pagination={false}
                size="middle"
              />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Card title="Cost Analysis" size="small">
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Labor + Waiting Cost"
                      value={cost_analysis?.cost_labor_plus_waiting || 0}
                      precision={2}
                      prefix="$"
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Total Cost Rate"
                      value={cost_analysis?.total_cost_rate || 0}
                      precision={2}
                      prefix="$"
                    />
                  </Col>
                </Row>
              </Card>

              {results.chart_url && (
                <Card title="Traffic Map / Flow Analysis" size="small" style={{ textAlign: 'center' }}>
                  <Image
                    src={results.chart_url}
                    alt="Erlang-k Analysis Chart"
                    fallback="https://via.placeholder.com/400x300?text=Erlang-k+Flow+Graph"
                    style={{ maxHeight: '300px' }}
                  />
                </Card>
              )}
            </Space>
          </Col>
        </Row>
      </Space>
    );
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card
        title={
          <Title level={4} style={{ margin: 0 }}>
            <CalculatorOutlined /> {fileName || "Erlang-k Service Times Analysis"}
          </Title>
        }
        className="calculator-card"
        style={{ borderRadius: '12px', overflow: 'hidden' }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          items={[
            {
              key: "input",
              label: (<span><TableOutlined /> Inputs</span>),
              children: renderInputForm()
            },
            {
              key: "output",
              label: (<span><LineChartOutlined /> Results</span>),
              disabled: !results,
              children: renderOutputTable()
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default ErlangKServiceTimes;
